import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import DashboardRail from "../components/dashboard/DashboardRail";
import DashboardPulse from "../components/dashboard/DashboardPulse";
import DashboardManageGrid from "../components/dashboard/DashboardManageGrid";
import DashboardTransactionsPanel from "../components/dashboard/DashboardTransactionsPanel";
import DashboardLeaderboardPanel from "../components/dashboard/DashboardLeaderboardPanel";
import DepositModal from "../components/payments/DepositModal";
import WithdrawModal from "../components/payments/WithdrawModal";
import ConvertModal from "../components/payments/ConvertModal";
import { patchAppUser, selectAppUser } from "../redux/appAuthSlice";
import { resolveWalletBalances } from "../utils/dashboardHelpers";
import {
  fetchDashboardProfile,
  fetchDashboardTransactions,
  fetchDashboardTxAnalytics,
  setDashboardTxFilter,
} from "../redux/dashboardSlice";
import FeedRefreshBanner from "../../components/FeedRefreshBanner";
import { isNetworkOffline } from "../../utils/requireNetwork";
import "../dashboard.css";
import "../components/payments/payments.css";

const Dashboard = () => {
  const dispatch = useDispatch();
  const authUser = useSelector(selectAppUser);
  const userId = authUser?._id ?? authUser?.id;

  const {
    profile,
    profileStatus,
    transactions,
    transactionsStatus,
    transactionsError,
    txPagination,
    txFilter,
    loadingMoreTx,
    analyticsTx,
    analyticsTxStatus,
  } = useSelector((s) => s.dashboard);

  const [activeSection, setActiveSection] = useState("workspace");
  const [transactionsTab, setTransactionsTab] = useState("analytics");
  const [refreshing, setRefreshing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [convertDirection, setConvertDirection] = useState(null);
  const [toast, setToast] = useState(null);

  const { cash, coin } = resolveWalletBalances(profile, authUser);
  const displayName = profile?.name || authUser?.name || "User";
  const initial = (displayName[0] || "U").toUpperCase();
  const paymentUser = profile
    ? { ...authUser, ...profile }
    : authUser;
  const cashRef = useRef(cash);
  const walletPollGenRef = useRef(0);
  cashRef.current = cash;

  const loadCore = useCallback(async ({ force = false, silent = false } = {}) => {
    if (!userId) return null;
    if (!force && isNetworkOffline()) return null;

    const hasCachedData = Boolean(profile || authUser);
    if (!silent) setIsUpdating(true);
    if (!hasCachedData) setRefreshFailed(false);

    try {
      if (silent) {
        const nextProfile = await dispatch(
          fetchDashboardProfile({ userId, force: true })
        ).unwrap();
        setRefreshFailed(false);
        return nextProfile;
      }

      await Promise.all([
        dispatch(fetchDashboardProfile({ userId, force })).unwrap(),
        dispatch(
          fetchDashboardTransactions({
            userId,
            type: "all",
            status: "all",
            page: 1,
            limit: 40,
            append: false,
            force,
          })
        ).unwrap(),
        dispatch(fetchDashboardTxAnalytics({ userId, force })).unwrap(),
      ]);
      setRefreshFailed(false);
      return null;
    } catch {
      if (hasCachedData) setRefreshFailed(true);
      return null;
    } finally {
      if (!silent) setIsUpdating(false);
    }
  }, [dispatch, userId, profile, authUser]);

  const loadTransactions = useCallback(
    (append = false) => {
      if (!userId) return Promise.resolve();
      const page = append ? (txPagination.currentPage || 1) + 1 : 1;
      return dispatch(
        fetchDashboardTransactions({
          userId,
          type: txFilter.type,
          status: txFilter.status,
          page,
          limit: 40,
          append,
        })
      );
    },
    [dispatch, userId, txFilter.type, txFilter.status, txPagination.currentPage]
  );

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (profileStatus !== "succeeded" || !profile) return;
    dispatch(
      patchAppUser({
        CASH: profile.CASH,
        COIN: profile.COIN,
      })
    );
  }, [profile, profileStatus, dispatch]);

  useEffect(() => {
    if (profile?.CASH != null || profile?.COIN != null) {
      dispatch(patchAppUser({ CASH: profile.CASH, COIN: profile.COIN }));
    }
  }, [dispatch, profile?.CASH, profile?.COIN]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 8000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const notify = (type, title, message) => {
    setToast({ type, title, message });
  };

  const handlePaymentSuccess = async (data) => {
    const previousCash = cashRef.current;
    const nextCash =
      data?.withdrawal?.balanceAfter ??
      data?.deposit?.balanceAfter ??
      data?.balanceAfter;

    if (nextCash != null) {
      dispatch(patchAppUser({ CASH: nextCash }));
    }

    await loadCore({ force: true });
    if (activeSection === "transactions") {
      await loadTransactions(false);
    }

    const isWithdrawal = Boolean(data?.withdrawal);
    if (isWithdrawal) return;

    const generation = ++walletPollGenRef.current;
    const delays = [2500, 4000, 6000, 8000, 10000, 12000];
    for (const delay of delays) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      if (generation !== walletPollGenRef.current) return;
      const nextProfile = await loadCore({ force: true, silent: true });
      const next = nextProfile?.CASH ?? cashRef.current;
      if (Number(next) !== Number(previousCash)) {
        await loadCore({ force: true });
        if (activeSection === "transactions") await loadTransactions(false);
        return;
      }
    }
  };

  const handleConversionSuccess = async (data) => {
    if (data?.balances) {
      dispatch(
        patchAppUser({ CASH: data.balances.cash, COIN: data.balances.coin })
      );
    }
    await loadCore({ force: true });
    if (activeSection === "transactions") {
      await loadTransactions(false);
    }
  };

  useEffect(() => {
    if (activeSection !== "transactions" || !userId) return;
    dispatch(
      fetchDashboardTransactions({
        userId,
        type: txFilter.type,
        status: txFilter.status,
        page: 1,
        limit: 40,
        append: false,
      })
    );
  }, [activeSection, userId, txFilter.type, txFilter.status, dispatch]);

  const handleSectionChange = (section) => {
    if (section === "transactions") {
      setTransactionsTab("analytics");
    }
    setActiveSection(section);
  };

  const openTransactionsLedger = () => {
    setTransactionsTab("ledger");
    setActiveSection("transactions");
  };

  const handleRefresh = async () => {
    walletPollGenRef.current += 1;
    setRefreshing(true);
    try {
      await loadCore({ force: true });
      if (activeSection === "transactions") await loadTransactions(false);
    } finally {
      setRefreshing(false);
    }
  };

  const loadingProfile = profileStatus === "loading" && !profile && !authUser;

  if (loadingProfile) {
    return (
      <div className="dash-page">
        <div className="dash-desk-layout">
          <aside className="dash-desk-rail dash-desk-rail-skeleton" aria-hidden />
          <main className="dash-desk-main">
            <div className="dash-loading">
              <Loader2 size={20} className="animate-spin" />
              <span>Loading…</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-desk-layout">
        <DashboardRail
          onRefresh={handleRefresh}
          refreshing={refreshing}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          displayName={displayName}
          userLevel={profile?.userLevel}
          initial={initial}
        />

        <main className="dash-desk-main">
          <FeedRefreshBanner
            isUpdating={isUpdating}
            refreshFailed={refreshFailed}
            onRetry={handleRefresh}
          />
          <DashboardPulse
            cash={cash}
            coin={coin}
            analyticsTx={analyticsTx}
            refreshing={refreshing || isUpdating}
            onRefresh={handleRefresh}
            onDeposit={() => setShowDepositModal(true)}
            onWithdraw={() => setShowWithdrawModal(true)}
            onBuyCoin={() => setConvertDirection("buy")}
            onSwapCoin={() => setConvertDirection("swap")}
          />

          <div key={activeSection} className="dash-desk-stage dash-body-enter">
            {activeSection === "workspace" && (
              <DashboardManageGrid
                transactions={transactions}
                transactionsLoading={transactionsStatus === "loading"}
                onViewActivity={openTransactionsLedger}
              />
            )}
            {activeSection === "transactions" && (
              <DashboardTransactionsPanel
                defaultView={transactionsTab}
                transactions={transactions}
                analyticsTx={analyticsTx}
                analyticsLoading={analyticsTxStatus === "loading"}
                loading={transactionsStatus === "loading"}
                loadingMore={loadingMoreTx}
                error={transactionsError}
                filter={txFilter}
                pagination={txPagination}
                onFilterChange={(patch) => dispatch(setDashboardTxFilter(patch))}
                onRefresh={() => loadTransactions(false)}
                onLoadMore={() => loadTransactions(true)}
              />
            )}
            {activeSection === "leaderboard" && (
              <DashboardLeaderboardPanel embedded />
            )}
          </div>
        </main>
      </div>

      <DepositModal
        open={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        userId={userId}
        user={paymentUser}
        onNotify={notify}
        onSuccess={handlePaymentSuccess}
      />

      <WithdrawModal
        open={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        userId={userId}
        user={paymentUser}
        currentBalance={cash}
        onNotify={notify}
        onSuccess={handlePaymentSuccess}
      />

      <ConvertModal
        open={convertDirection != null}
        direction={convertDirection || "buy"}
        onClose={() => setConvertDirection(null)}
        userId={userId}
        cash={cash}
        coin={coin}
        onNotify={notify}
        onSuccess={handleConversionSuccess}
      />

      {toast ? (
        <div
          className={`dash-toast dash-toast-${toast.type === "error" ? "error" : "success"}`}
          role="status"
        >
          <strong>{toast.title}</strong>
          <span>{toast.message}</span>
        </div>
      ) : null}
    </div>
  );
};

export default Dashboard;
